import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Plus, Unlink } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toastApiError } from "../../../api/apiError"
import {
  useArticleProducts,
  useRemoveProductArticle,
} from "../../../api/productsApi"
import type { ProductRow } from "../../../api/types/masterData"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { LinkArticleToProductDialog } from "../products/LinkArticleToProductDialog"

export function ArticleProductsSection() {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data, hasNextPage, fetchNextPage, isLoading } = useArticleProducts(
    id,
    { limit: 20 },
  )
  const { mutate: removeLink, isPending: removing } = useRemoveProductArticle()

  const products = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  if (!id) return null

  const columns: SubTableColumn<ProductRow>[] = [
    {
      key: "productNumber",
      header: translate("MC_MD_PANEL_COL_PRODUCT"),
      cell: (p) => <span className="font-mono">{p.productNumber}</span>,
      className: "w-40",
    },
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_DESCRIPTION"),
      cell: (p) => <span className="block max-w-xs truncate">{p.name}</span>,
    },
    {
      key: "unlink",
      header: "",
      cell: (p) => (
        <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-grey5 hover:text-white"
            aria-label={translate("MC_MD_PANEL_ARTICLES_UNLINK")}
            disabled={removing}
            onClick={(e) => {
              e.stopPropagation()
              removeLink(
                { productId: p.id, articleId: id },
                {
                  onError: (err) =>
                    toastApiError(err, translate, "MC_MD_SAVE_ERROR"),
                },
              )
            }}
          >
            <Unlink size={14} />
          </Button>
        </PermissionGate>
      ),
      className: "w-10 text-right",
    },
  ]

  const addButton = (
    <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <Plus />
        {translate("MC_MD_PANEL_ARTICLE_PRODUCTS_LINK")}
      </Button>
    </PermissionGate>
  )

  return (
    <>
      <EntitySubTable
        title={translate("MC_MD_PANEL_ARTICLE_PRODUCTS_TITLE")}
        items={products}
        columns={columns}
        keyOf={(p) => p.id}
        hasMore={!!hasNextPage}
        onFetchMore={fetchNextPage}
        isLoading={isLoading}
        total={total}
        onRowClick={(p) => navigate(`../products?id=${p.id}&source=nav`)}
        action={addButton}
      />
      <LinkArticleToProductDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        articleId={id}
      />
    </>
  )
}
